import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, IconButton, Pressable, useBreakpointValue, useMediaQuery, useTheme, VStack } from 'native-base';
import { filter, find, flatMap, isEmpty, map, range, reduce, sortBy, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Expand } from 'lucide-react';
import moment from 'moment';

import { ButtonGroup, UserCircleIcon, Edit05Icon, Modal, Text, Chip } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import {
  CleaningScheduleTypeEnum,
  CleaningTaskResetTypeEnum,
  CleaningTaskStatusEnum,
  ResetCleaningTasksRequest,
} from '@pimm/services/lib/sms-workforce/types';
import { ResetCleaningTasks } from '@pimm/services/lib/sms-workforce/services';
import { formatTo, formatToISOString, stringToDateLocal } from '@app/utils/date-formatter';
import { useSiteTime } from '@app/features/store-core';
import { CleaningTasksTable, useCleaningAssignments, useCleaningToast, useGetCleaningPlan } from '@app/features/cleaning-tasks';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { CompletionIcon } from '@app/components/shared';
import { CleaningPlan, CleaningTaskItem } from '../reducers';
import { CleaningDocuments } from './cleaning-documents';
import { CleaningCategoryNav } from './cleaning-category-nav';
import { getScheduleColor } from '../utils';
import { ResetConfirmationAction } from './reset-confirmation-modal';
import { EditModeType } from './cleaning-tasks-editable-view';
import PopoverScheduleMenu from './popover-schedule-menu';
import PopoverResetMenu from './popover-reset-menu';
import PopoverAutoAssignMenu from './popover-auto-assign-menu';
import PopoverFilterMenu from './popover-filter-menu';
import MonthTogglePills from './month-toggle-pills';

type CleaningFormProps = {
  scheduleType?: CleaningScheduleTypeEnum;
  isEditable?: boolean;
  initialSelectedDate?: string;
  onToggleIsEditable?: (scheduleType: CleaningScheduleTypeEnum, selectedDate?: string) => void;
  onChangeScheduleType?: (scheduleType: CleaningScheduleTypeEnum) => void;
  /** Global "Pending Only" toolbar toggle — hides Complete and N/A tasks. */
  showOnlyPending?: boolean;
};

const TAB_ITEMS = [
  { title: 'weekly_tasks', value: CleaningScheduleTypeEnum.Weekly },
  { title: 'monthly_tasks', value: CleaningScheduleTypeEnum.Monthly },
];

export const CleaningForm = ({
  scheduleType: initialScheduleType,
  onToggleIsEditable,
  onChangeScheduleType,
  showOnlyPending,
  ...props
}: CleaningFormProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  const { assignments, week, siteId, refetchAssignments } = useCleaningAssignments();
  const siteTime = useSiteTime();
  const { showToast } = useCleaningToast();
  const modalCleaningTask = useModalFocus<CleaningTaskItem>();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const is2xlScreen = useBreakpointValue({ base: false, '2xl': true });
  const [scheduleType, setScheduleType] = useState<CleaningScheduleTypeEnum>(initialScheduleType ?? CleaningScheduleTypeEnum.Daily);
  const [activeMonth, setActiveMonth] = useState(0);
  const [editMode, setEditMode] = useState<EditModeType>('normal');
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
  const [activeMenu, setActiveMenu] = useState<'schedule' | 'auto-assign' | 'reset' | 'filter' | null>(null);
  // popupMode: when set, opens the same sub-mode UI from Edit Mode (Bulk
  // Schedule / Copy Last Week / Auto-Assign) inside a Modal, so the user can
  // perform the action without fully entering Edit Mode.
  const [popupMode, setPopupMode] = useState<EditModeType | null>(null);
  // Latches the last non-null popupMode so CleaningTasksTable stays mounted
  // during the modal's fadeOut animation (prevents blank-flash on close).
  const renderedPopupModeRef = useRef<EditModeType | null>(null);
  if (popupMode !== null) renderedPopupModeRef.current = popupMode;
  const [activeStatusFilters, setActiveStatusFilters] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const prevWeekRef = useRef<Date>();
  const prevScheduleTypeRef = useRef<CleaningScheduleTypeEnum>();
  const prevInitialScheduleTypeRef = useRef<CleaningScheduleTypeEnum | undefined>(initialScheduleType);

  const isCrossover = useMemo(
    () => scheduleType === CleaningScheduleTypeEnum.Monthly && week.startDate.getMonth() !== week.endDate.getMonth(),
    [scheduleType, week.startDate, week.endDate],
  );

  const monthLabels = useMemo(
    () => (isCrossover ? [formatTo(week.startDate, 'MMM'), formatTo(week.endDate, 'MMM')] : []),
    [isCrossover, week.startDate, week.endDate],
  );

  const [secondMonthResult, setSecondMonthParams] = useGetCleaningPlan({ queryKey: 'GetCleaningPlanMonthlyCrossover' });

  const selectedDate = useMemo(
    () =>
      range(7).reduce((_date: Date | undefined, day) => {
        const today = siteTime.today();
        const date = moment(week.startDate).add(day, 'day').toDate();
        const { startTime, endTime } = siteTime.toStartEndOfBlock(date);
        if (today >= startTime! && today < endTime!) return date;
        return _date;
      }, week.startDate as Date | undefined),
    [week.startDate, siteTime],
  );

  const [tabFocus, setTabFocus] = useState<string>(
    props.initialSelectedDate || (selectedDate ? formatToISOString(selectedDate) : CleaningScheduleTypeEnum.Weekly),
  );
  const prevTabFocusRef = useRef<string>(tabFocus);

  const dayButtons = useMemo(
    () =>
      range(7).map(day => {
        const date = week.startDate.addDays(day);
        const dateKey = formatToISOString(date);
        const tasks = find(
          assignments,
          plan =>
            plan.date.getMonth() === date.getMonth() &&
            plan.date.getDate() === date.getDate() &&
            plan.scheduleType === CleaningScheduleTypeEnum.Daily,
        )?.tasks;

        const completedCount =
          tasks?.filter(
            t => t.assignee?.status === CleaningTaskStatusEnum.Complete || t.assignee?.status === CleaningTaskStatusEnum.NotApplicable,
          ).length ?? 0;
        const isComplete = completedCount > 0 && completedCount === tasks?.length;

        const nonNADayTasks = tasks?.filter(t => t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable) ?? [];
        const dayTotal = nonNADayTasks.length;
        const dayAssigned = nonNADayTasks.filter(t => !!t.assignee?.employeeId).length;

        return {
          date,
          dateKey,
          completedCount,
          isComplete,
          dayAssigned,
          dayTotal,
          isAllAssigned: dayTotal > 0 && dayAssigned === dayTotal,
        };
      }),
    [week.startDate, assignments],
  );

  const { cleaningPlan, isEditable: isPlanEditable } = useMemo(() => {
    const plan = assignments.find(
      p => p.scheduleType === scheduleType && (scheduleType !== CleaningScheduleTypeEnum.Daily || formatToISOString(p.date) === tabFocus),
    );
    if (!plan) return { cleaningPlan: undefined, isEditable: false };

    const today = siteTime.today();
    const endTime =
      plan.scheduleType === CleaningScheduleTypeEnum.Monthly
        ? siteTime.toStartEndOfBlock(moment(today).endOf('month').toDate()).endTime
        : siteTime.toStartEndOfBlock(week.endDate).endTime;

    return { cleaningPlan: plan, isEditable: !!endTime && today < endTime };
  }, [assignments, scheduleType, tabFocus, siteTime, week.endDate]);

  const secondMonthCleaningPlan = useMemo<CleaningPlan | undefined>(() => {
    if (!secondMonthResult.data || !isCrossover) return undefined;
    const monthlyRaw = secondMonthResult.data.find(p => p.scheduleType === 'Monthly');
    if (!monthlyRaw) return undefined;

    return {
      date: monthlyRaw.date ?? week.endDate.addDays(1),
      scheduleType: CleaningScheduleTypeEnum.Monthly,
      tasks: (monthlyRaw.categories ?? []).reduce<CleaningTaskItem[]>((acc, category) => {
        (category.schedules ?? []).forEach(schedule => {
          sortBy(schedule.cleaningTasks ?? [], ['seq']).forEach(task => {
            const _task = task.cleaningTaskAssignment;
            acc.push({
              id: task.cleaningTaskId,
              assignee: _task
                ? {
                    id: _task.id,
                    employeeId: _task.employeeId,
                    firstName: _task.firstName,
                    lastName: _task.lastName,
                    dueDate: stringToDateLocal(_task.dueDate),
                    status: _task.cleaningTaskStatus,
                    statusDate: stringToDateLocal(_task.statusDate),
                    autoAssignedDateUtc: _task.autoAssignedDateUtc,
                    autoScheduledDateUtc: _task.autoScheduledDateUtc,
                    isAutoAssigned: _task.isAutoAssigned,
                    isAutoScheduled: _task.isAutoScheduled,
                  }
                : undefined,
              category: category.title ?? undefined,
              categoryTranslations: category.translations ?? undefined,
              description: task.description ?? undefined,
              descriptionTranslations: task.descriptionTranslations ?? undefined,
              linkedDoc: task.linkedDoc ?? undefined,
              positionId: task.positionId ?? undefined,
              title: task.title ?? undefined,
              translations: task.titleTranslations ?? undefined,
            });
          });
        });
        return acc;
      }, []),
    };
  }, [secondMonthResult.data, isCrossover, week.endDate]);

  const monthlyTabStats = useMemo(() => {
    const monthlyPlan = isCrossover && activeMonth === 1 ? secondMonthCleaningPlan ?? cleaningPlan : cleaningPlan;
    const nonNATasks = monthlyPlan?.tasks.filter(t => t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable) ?? [];
    return {
      completed: nonNATasks.filter(t => !!t.assignee?.employeeId).length,
      total: nonNATasks.length,
    };
  }, [isCrossover, activeMonth, cleaningPlan, secondMonthCleaningPlan]);

  const activeCleaningPlan = isCrossover && activeMonth === 1 ? secondMonthCleaningPlan ?? cleaningPlan : cleaningPlan;

  const filteredCleaningPlan = useMemo(() => {
    if (!activeCleaningPlan) return activeCleaningPlan;
    if (activeStatusFilters.size === 0 && !showOnlyPending) return activeCleaningPlan;

    let tasks = activeCleaningPlan.tasks;

    if (activeStatusFilters.size > 0) {
      tasks = tasks.filter(task => {
        const assignee = task.assignee;
        return Array.from(activeStatusFilters).some(key => {
          if (key === 'completed') return assignee?.status === CleaningTaskStatusEnum.Complete;
          if (key === 'assigned') return !!assignee?.employeeId;
          if (key === 'unassigned') return !assignee?.employeeId;
          if (key === 'unscheduled') return !assignee?.dueDate;
          if (key === 'na') return assignee?.status === CleaningTaskStatusEnum.NotApplicable;
          return false;
        });
      });
    }

    // Quick "Pending Only" toolbar toggle — hide Complete + NotApplicable across
    // every column. Applied AFTER the status-filter popover so the two compose:
    // e.g. choosing "Unassigned" in the popover while Pending Only is on shows
    // unassigned-and-incomplete tasks.
    if (showOnlyPending) {
      tasks = tasks.filter(task => {
        const status = task.assignee?.status;
        return status !== CleaningTaskStatusEnum.Complete && status !== CleaningTaskStatusEnum.NotApplicable;
      });
    }

    return { ...activeCleaningPlan, tasks };
  }, [activeCleaningPlan, activeStatusFilters, showOnlyPending]);

  const { completedCount, totalCount } = useMemo(() => {
    if (!activeCleaningPlan) return { completedCount: 0, totalCount: 0 };
    const nonNATasks = activeCleaningPlan.tasks.filter(t => t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable);
    return {
      completedCount: nonNATasks.filter(t => t.assignee?.status === CleaningTaskStatusEnum.Complete).length,
      totalCount: nonNATasks.length,
    };
  }, [activeCleaningPlan]);

  const { eligibleForBulk, eligibleForCopyLastWeek, eligibleForAutoAssign, hasAutoAssigned, hasAutoScheduled } = useMemo(() => {
    if (!activeCleaningPlan) {
      return {
        eligibleForBulk: [],
        eligibleForCopyLastWeek: [],
        eligibleForAutoAssign: [],
        hasAutoAssigned: false,
        hasAutoScheduled: false,
      };
    }

    const tasks = activeCleaningPlan.tasks;
    const eligible = tasks.filter(
      t =>
        t.assignee?.status !== CleaningTaskStatusEnum.Complete &&
        t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable &&
        !t.assignee?.employeeId,
    );

    return {
      eligibleForBulk: eligible,
      eligibleForCopyLastWeek: eligible.filter(t => !t.assignee?.isAutoScheduled),
      eligibleForAutoAssign: eligible.filter(t => !!t.assignee?.dueDate),
      hasAutoAssigned: tasks.some(
        t =>
          !!t.assignee?.employeeId &&
          t.assignee?.status !== CleaningTaskStatusEnum.Complete &&
          t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable,
      ),
      hasAutoScheduled: tasks.some(
        t =>
          (!!t.assignee?.dueDate || !!t.assignee?.employeeId) &&
          t.assignee?.status !== CleaningTaskStatusEnum.Complete &&
          t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable,
      ),
    };
  }, [activeCleaningPlan]);

  const scheduleTypeStats = useMemo(
    () =>
      reduce(
        assignments,
        (acc, plan) => {
          const nonNA = plan.tasks.filter(t => t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable);
          acc[plan.scheduleType].completed += nonNA.filter(t => !!t.assignee?.employeeId).length;
          acc[plan.scheduleType].total += nonNA.length;
          return acc;
        },
        {
          [CleaningScheduleTypeEnum.None]: { completed: 0, total: 0 },
          [CleaningScheduleTypeEnum.Daily]: { completed: 0, total: 0 },
          [CleaningScheduleTypeEnum.Weekly]: { completed: 0, total: 0 },
          [CleaningScheduleTypeEnum.Monthly]: { completed: 0, total: 0 },
          [CleaningScheduleTypeEnum.Yearly]: { completed: 0, total: 0 },
        } as Record<CleaningScheduleTypeEnum, { completed: number; total: number }>,
      ),
    [assignments],
  );

  const hasMonthlyTasks = useMemo(
    () => assignments.some(p => p.scheduleType === CleaningScheduleTypeEnum.Monthly && p.tasks.length > 0),
    [assignments],
  );

  const categories = useMemo(() => {
    if (!cleaningPlan) return [];
    const cats = uniq(map(cleaningPlan.tasks, t => t.category).filter(Boolean)) as string[];
    return cats.length === 0 && cleaningPlan.scheduleType === 'Daily' ? ['Daypart 1-3', 'Daypart 4-6'] : cats;
  }, [cleaningPlan]);

  const scheduleTypeOptions = useMemo(
    () => [
      { label: t('cleaning_task:daily'), value: CleaningScheduleTypeEnum.Daily },
      { label: t('cleaning_task:weekly'), value: CleaningScheduleTypeEnum.Weekly },
      { label: t('cleaning_task:monthly'), value: CleaningScheduleTypeEnum.Monthly },
    ],
    [locale],
  );

  const tabColor = getScheduleColor(scheduleType);
  const allDone = totalCount > 0 && completedCount === totalCount;
  const partial = completedCount > 0 && completedCount < totalCount;

  useEffect(() => {
    if (isCrossover) setSecondMonthParams({ date: week.endDate.addDays(1), siteId });
  }, [isCrossover, week.endDate, siteId]);

  useEffect(() => {
    if (initialScheduleType && initialScheduleType !== prevInitialScheduleTypeRef.current) {
      prevInitialScheduleTypeRef.current = initialScheduleType;
      setScheduleType(initialScheduleType);
    }
  }, [initialScheduleType]);

  useEffect(() => {
    setActiveMonth(0);
  }, [week.startDate, scheduleType]);

  useEffect(() => {
    const weekChanged = prevWeekRef.current && prevWeekRef.current.getTime() !== week.startDate.getTime();
    const switchedToDaily =
      prevScheduleTypeRef.current !== CleaningScheduleTypeEnum.Daily && scheduleType === CleaningScheduleTypeEnum.Daily;

    prevWeekRef.current = week.startDate;
    prevScheduleTypeRef.current = scheduleType;

    if (scheduleType === CleaningScheduleTypeEnum.Daily && selectedDate && (weekChanged || switchedToDaily)) {
      setTabFocus(formatToISOString(selectedDate));
    }
  }, [scheduleType, selectedDate, week.startDate]);

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) setActiveCategory(categories[0]);
  }, [categories, activeCategory]);

  // Re-scope selections when switching day tabs during an active selection mode.
  // Pre-selects the new day's eligible tasks (same as mode-entry behavior).
  // prevTabFocusRef guards against re-running on unrelated renders.
  useEffect(() => {
    if (prevTabFocusRef.current === tabFocus) return;
    prevTabFocusRef.current = tabFocus;
    if (editMode === 'normal') return;
    if (editMode === 'bulk') setSelectedTaskIds(new Set(eligibleForBulk.map(t => t.id!)));
    else if (editMode === 'copy-last-week') setSelectedTaskIds(new Set(eligibleForCopyLastWeek.map(t => t.id!)));
    else if (editMode === 'auto-assign') setSelectedTaskIds(new Set(eligibleForAutoAssign.map(t => t.id!)));
  }, [tabFocus]);

  const toggleStatusFilter = (key: string) =>
    setActiveStatusFilters(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleEnterEditMode = (mode: EditModeType, tasks: CleaningTaskItem[] = []) => {
    setEditMode(mode);
    setSelectedTaskIds(new Set(tasks.map(t => t.id!)));
    setActiveMenu(null);
    setActiveStatusFilters(new Set());
  };

  // Section-header compact buttons use this to open a sub-mode in a Modal popup
  // (instead of fully entering Edit Mode). Same internal state so CleaningTasksTable
  // renders the right sub-mode, but props.isEditable is not toggled.
  const handleEnterPopupMode = (mode: EditModeType, tasks: CleaningTaskItem[] = []) => {
    setEditMode(mode);
    setSelectedTaskIds(new Set(tasks.map(t => t.id!)));
    setActiveMenu(null);
    setActiveStatusFilters(new Set());
    setPopupMode(mode);
  };

  const handleExitPopupMode = () => {
    setPopupMode(null);
    setEditMode('normal');
    setSelectedTaskIds(new Set());
    setActiveStatusFilters(new Set());
  };

  const handleExitEditMode = () => {
    setEditMode('normal');
    setActiveStatusFilters(new Set());
    setSelectedTaskIds(new Set());
  };

  const handleChangeDay = (day: string) => {
    setScheduleType(CleaningScheduleTypeEnum.Daily);
    setTabFocus(day);
  };

  const handleChangeScheduleType = (type: CleaningScheduleTypeEnum) => {
    setScheduleType(type);
    if (type !== CleaningScheduleTypeEnum.Daily) setTabFocus(type);
    setActiveCategory(null);
    setEditMode('normal');
    onChangeScheduleType?.(type);
  };

  const resetCleaningTasksMutation = useMutation({
    mutationFn: ResetCleaningTasks,
    onSuccess: (_, variables) => {
      refetchAssignments();
      showToast(
        'success',
        <Text size="md" color="white" fontWeight={600}>
          {variables.resetType === CleaningTaskResetTypeEnum.ClearAssignments
            ? t('cleaning_task:auto_assignments_reset')
            : t('cleaning_task:auto_schedules_and_assignments_reset')}
        </Text>,
      );
    },
    onError: () => {
      showToast(
        'error',
        <Box>
          <Text size="lg" flexShrink={1} fontWeight={700} color="white" lineHeight="xs">
            {t('cleaning_task:confirmation_failed')}
          </Text>
          <Text size="md" color="white" fontWeight={600}>
            {t('cleaning_task:sorry_something_went_wrong')}
          </Text>
        </Box>,
      );
    },
  });

  const handleResetConfirm = (action: ResetConfirmationAction) => {
    if (!cleaningPlan) return;
    const resetType =
      action === 'assignments' ? CleaningTaskResetTypeEnum.ClearAssignments : CleaningTaskResetTypeEnum.ClearSchedulesAndAssignments;
    const isIncomplete = (t: CleaningTaskItem) =>
      t.assignee?.status !== CleaningTaskStatusEnum.Complete && t.assignee?.status !== CleaningTaskStatusEnum.NotApplicable;

    const cleaningTaskIds = cleaningPlan.tasks
      .filter(
        t => isIncomplete(t) && (action === 'assignments' ? !!t.assignee?.employeeId : !!t.assignee?.dueDate || !!t.assignee?.employeeId),
      )
      .map(t => t.id!)
      .filter(Boolean);
    const payload: ResetCleaningTasksRequest = {
      siteId,
      scheduleType: cleaningPlan.scheduleType,
      businessDate: formatToISOString(cleaningPlan.date),
      resetType,
      cleaningTaskIds,
    };
    resetCleaningTasksMutation.mutate(payload);
  };

  const handleClearFilters = () => {
    setActiveStatusFilters(new Set());
  };

  return (
    <VStack h="full" w="full" overflow="hidden">
      {props.isEditable ? (
        <VStack h="full" w="full" overflow="hidden">
          {/* Title Row */}
          <VStack w="full" borderBottomWidth={1} borderColor="gray.200">
            <HStack py={2} px={4} alignItems="center" justifyContent="space-between">
              {/* LEFT: schedule tabs + day buttons grouped together */}
              <HStack alignItems="center">
                <ButtonGroup
                  value={scheduleType}
                  onChange={val => handleChangeScheduleType(val as CleaningScheduleTypeEnum)}
                  bg="gray.100"
                  borderWidth={1}
                  borderColor="gray.200"
                  rounded="lg"
                  p={0.5}
                >
                  {scheduleTypeOptions
                    .filter(option => option.value !== CleaningScheduleTypeEnum.Monthly || hasMonthlyTasks)
                    .map(option => {
                      const stats =
                        option.value === CleaningScheduleTypeEnum.Monthly && isCrossover
                          ? monthlyTabStats
                          : scheduleTypeStats[option.value];
                      const isDone = stats.total > 0 && stats.completed === stats.total;
                      const isPartial = stats.completed > 0 && stats.completed < stats.total;
                      const color = getScheduleColor(option.value);

                      return (
                        <ButtonGroup.Item key={option.value} value={option.value} px={3} py={1.5} minW="90px">
                          <HStack alignItems="center" space={1.5}>
                            <Text size="md" fontWeight={600} color="gray.800">
                              {option.label}
                            </Text>
                            {isDone ? (
                              <UserCircleIcon color={colors[color][500]} />
                            ) : (
                              <Chip
                                value={`${stats.completed}/${stats.total}`}
                                _container={{
                                  backgroundColor: isPartial ? `${color}.50` : 'gray.100',
                                  borderColor: isPartial ? `${color}.200` : 'gray.300',
                                  borderWidth: 1,
                                  px: 1.5,
                                  py: 1,
                                }}
                                letterSpacing={1}
                                fontWeight={600}
                                textAlign="center"
                                size="sm"
                                color={isPartial ? `${color}.700` : 'gray.500'}
                              />
                            )}
                          </HStack>
                        </ButtonGroup.Item>
                      );
                    })}
                </ButtonGroup>

                {/* Day buttons — inline beside tabs on large screens only */}
                {scheduleType === CleaningScheduleTypeEnum.Daily && (
                  <HStack display={{ base: 'none', '2xl': 'flex' }} alignItems="center">
                    <Box w="1px" h={5} bg="gray.200" mx={3} />
                    <ButtonGroup
                      value={tabFocus}
                      onChange={handleChangeDay}
                      bg="gray.100"
                      borderWidth={1}
                      borderColor="gray.200"
                      rounded="lg"
                      p={0.5}
                    >
                      {dayButtons.map(({ date, dateKey, dayAssigned, dayTotal, isAllAssigned }) => (
                        <ButtonGroup.Item key={dateKey} value={dateKey} _text={{ fontSize: 'md', fontWeight: 600 }} minW="70px">
                          <HStack alignItems="center" space={2}>
                            <Text size="md" fontWeight={600} color="gray.800">
                              {formatTo(date, 'ddd')}
                            </Text>
                            {isAllAssigned ? (
                              <UserCircleIcon color={colors.blue[500]} />
                            ) : (
                              <Chip
                                value={`${dayAssigned}/${dayTotal}`}
                                _container={{
                                  backgroundColor: dayAssigned > 0 ? 'blue.50' : 'gray.100',
                                  borderColor: dayAssigned > 0 ? 'blue.200' : 'gray.300',
                                  borderWidth: 1,
                                  px: 2,
                                  py: 1,
                                }}
                                letterSpacing={1}
                                fontWeight={600}
                                textAlign="center"
                                size="sm"
                                color={dayAssigned > 0 ? 'blue.700' : 'gray.500'}
                              />
                            )}
                          </HStack>
                        </ButtonGroup.Item>
                      ))}
                    </ButtonGroup>
                  </HStack>
                )}
              </HStack>

              {/* RIGHT: single child so space-between always has exactly 2 anchors */}
              <HStack alignItems="center" space={2}>
                {/* Small screen: day buttons (Daily only) */}
                {scheduleType === CleaningScheduleTypeEnum.Daily && (
                  <ButtonGroup
                    display={{ base: 'flex', '2xl': 'none' }}
                    value={tabFocus}
                    onChange={handleChangeDay}
                    bg="gray.100"
                    borderWidth={1}
                    borderColor="gray.200"
                    rounded="lg"
                    p={0.5}
                  >
                    {dayButtons.map(({ date, dateKey, dayAssigned, dayTotal, isAllAssigned }) => (
                      <ButtonGroup.Item key={dateKey} value={dateKey} _text={{ fontSize: 'md', fontWeight: 600 }} minW="70px">
                        <HStack alignItems="center" space={2}>
                          <Text size="md" fontWeight={600} color="gray.800">
                            {formatTo(date, 'ddd')}
                          </Text>
                          {isAllAssigned ? (
                            <UserCircleIcon color={colors.blue[500]} />
                          ) : (
                            <Chip
                              value={`${dayAssigned}/${dayTotal}`}
                              _container={{
                                backgroundColor: dayAssigned > 0 ? 'blue.50' : 'gray.100',
                                borderColor: dayAssigned > 0 ? 'blue.200' : 'gray.300',
                                borderWidth: 1,
                                px: 2,
                                py: 1,
                              }}
                              letterSpacing={1}
                              fontWeight={600}
                              textAlign="center"
                              size="sm"
                              color={dayAssigned > 0 ? 'blue.700' : 'gray.500'}
                            />
                          )}
                        </HStack>
                      </ButtonGroup.Item>
                    ))}
                  </ButtonGroup>
                )}

                {/* Large screen: action buttons */}
                {editMode === 'normal' && (
                  <HStack display={{ base: 'none', '2xl': 'flex' }} space={2} alignItems="center">
                    <PopoverScheduleMenu
                      isOpen={is2xlScreen && activeMenu === 'schedule'}
                      onOpen={() => setActiveMenu('schedule')}
                      onClose={() => setActiveMenu(null)}
                      copyEligibleCount={eligibleForCopyLastWeek.length}
                      bulkEligibleCount={eligibleForBulk.length}
                      onEnterCopyLastWeekMode={() => handleEnterEditMode('copy-last-week', eligibleForCopyLastWeek)}
                      onEnterBulkMode={() => handleEnterEditMode('bulk', eligibleForBulk)}
                    />

                    <PopoverAutoAssignMenu
                      isOpen={is2xlScreen && activeMenu === 'auto-assign'}
                      onOpen={() => setActiveMenu('auto-assign')}
                      onClose={() => setActiveMenu(null)}
                      tasks={cleaningPlan?.tasks ?? []}
                      eligibleForAutoAssign={eligibleForAutoAssign}
                      onEnterAutoAssignMode={() => handleEnterEditMode('auto-assign', eligibleForAutoAssign)}
                    />

                    <Box w="1px" h={6} bg="gray.200" />

                    <PopoverResetMenu
                      isOpen={is2xlScreen && activeMenu === 'reset'}
                      onOpen={() => setActiveMenu('reset')}
                      onClose={() => setActiveMenu(null)}
                      hasAutoAssigned={hasAutoAssigned}
                      hasAutoScheduled={hasAutoScheduled}
                      onConfirmReset={handleResetConfirm}
                    />
                  </HStack>
                )}
              </HStack>
            </HStack>

            <HStack px={4} py={2} borderTopWidth={1} borderColor="gray.200" alignItems="center" justifyContent="space-between">
              <Text size="lg" fontWeight={600} color="gray.900">
                {t('cleaning_task:task_assignment')}
              </Text>

              <HStack space={2} alignItems="center" minH={8}>
                {scheduleType === CleaningScheduleTypeEnum.Monthly && isCrossover && (
                  <HStack bg="gray.100" borderWidth={1} borderColor="gray.200" rounded="lg" p={0.5} space={0.5}>
                    <MonthTogglePills labels={monthLabels} activeMonth={activeMonth} onPress={setActiveMonth} showCalendarIcon />
                  </HStack>
                )}

                {editMode === 'normal' && (
                  <>
                    <PopoverFilterMenu
                      isOpen={activeMenu === 'filter'}
                      onOpen={() => setActiveMenu('filter')}
                      onClose={() => setActiveMenu(null)}
                      activeStatusFilters={activeStatusFilters}
                      onToggleStatusFilter={toggleStatusFilter}
                      onClearFilters={handleClearFilters}
                    />

                    <HStack display={{ base: 'flex', '2xl': 'none' }} space={2} alignItems="center">
                      <Box w="1px" h={6} bg="gray.200" />

                      <PopoverScheduleMenu
                        isOpen={!is2xlScreen && activeMenu === 'schedule'}
                        onOpen={() => setActiveMenu('schedule')}
                        onClose={() => setActiveMenu(null)}
                        copyEligibleCount={eligibleForCopyLastWeek.length}
                        bulkEligibleCount={eligibleForBulk.length}
                        onEnterCopyLastWeekMode={() => handleEnterEditMode('copy-last-week', eligibleForCopyLastWeek)}
                        onEnterBulkMode={() => handleEnterEditMode('bulk', eligibleForBulk)}
                      />

                      <PopoverAutoAssignMenu
                        isOpen={!is2xlScreen && activeMenu === 'auto-assign'}
                        onOpen={() => setActiveMenu('auto-assign')}
                        onClose={() => setActiveMenu(null)}
                        tasks={cleaningPlan?.tasks ?? []}
                        eligibleForAutoAssign={eligibleForAutoAssign}
                        onEnterAutoAssignMode={() => handleEnterEditMode('auto-assign', eligibleForAutoAssign)}
                      />

                      <Box w="1px" h={6} bg="gray.200" />

                      <PopoverResetMenu
                        isOpen={!is2xlScreen && activeMenu === 'reset'}
                        onOpen={() => setActiveMenu('reset')}
                        onClose={() => setActiveMenu(null)}
                        hasAutoAssigned={hasAutoAssigned}
                        hasAutoScheduled={hasAutoScheduled}
                        onConfirmReset={handleResetConfirm}
                      />
                    </HStack>
                  </>
                )}
              </HStack>
            </HStack>
          </VStack>

          {/* Two Column Layout */}
          <HStack flex={1} h="full">
            <Box w="1/4" h="full" borderRightWidth={1} borderColor="gray.200" bg="white" overflow="hidden">
              <CleaningCategoryNav
                plan={filteredCleaningPlan}
                scheduleType={scheduleType}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </Box>

            <Box flex={1} h="full" overflow="hidden">
              {filteredCleaningPlan && (
                <CleaningTasksTable
                  key={`${scheduleType}:${activeCleaningPlan?.date.toISOString()}:${activeMonth}`}
                  // Temporarily allow editing even if not within the editable window
                  // as per sir tom's request to test the crossover week functionality — can re-enable the disabled state after testing
                  // added props.isEditable
                  isEditable={isPlanEditable || props.isEditable}
                  plan={filteredCleaningPlan}
                  editMode={editMode}
                  selectedTaskIds={selectedTaskIds}
                  onEditModeChange={setEditMode}
                  onSelectionChange={setSelectedTaskIds}
                  onExitEditMode={handleExitEditMode}
                  onOpenLinkDocument={modalCleaningTask.setOpen}
                  onClearFilters={handleClearFilters}
                />
              )}
            </Box>
          </HStack>
        </VStack>
      ) : (
        <VStack h="full" w="full" overflow="hidden" bg="gray.200" rounded="xl" style={{ elevation: 5 }}>
          {/* Small Screen Layout */}
          {isSmallScreen ? (
            <VStack m={3} flex={1} rounded="xl" shadow={2} borderColor="gray.200" overflow="hidden" h="full">
              <Box h={1} bg={`${tabColor}.500`} />
              <HStack
                key={locale}
                py={2}
                px={4}
                bg="white"
                borderBottomWidth={1}
                alignItems="center"
                justifyContent="space-between"
                w="full"
              >
                <ButtonGroup value={scheduleType === CleaningScheduleTypeEnum.Daily ? tabFocus : undefined} onChange={handleChangeDay}>
                  {dayButtons.map(({ date, dateKey, completedCount: dayCompletedCount, isComplete }) => (
                    <ButtonGroup.Item
                      key={dateKey}
                      pr={1.5}
                      endIcon={
                        <CompletionIcon
                          size={4}
                          variant={isComplete ? 'complete' : dayCompletedCount > 0 ? 'partial' : 'blank'}
                          color={dayCompletedCount === 0 ? colors.gray[500] : undefined}
                        />
                      }
                      value={dateKey}
                    >
                      {formatTo(date, 'ddd')}
                    </ButtonGroup.Item>
                  ))}
                </ButtonGroup>

                {/* Weekly/Monthly tabs */}
                <ButtonGroup
                  value={scheduleType !== CleaningScheduleTypeEnum.Daily ? scheduleType : undefined}
                  onChange={handleChangeScheduleType}
                >
                  {TAB_ITEMS.map(tab => {
                    const tasks = flatMap(
                      filter(assignments, _ => _.scheduleType === tab.value),
                      _ => _.tasks,
                    );
                    const total =
                      tab.value === CleaningScheduleTypeEnum.Monthly && isCrossover ? monthlyTabStats.total : tasks?.length ?? 0;
                    const completed =
                      tab.value === CleaningScheduleTypeEnum.Monthly && isCrossover
                        ? monthlyTabStats.completed
                        : tasks?.filter(
                            _ =>
                              _.assignee?.status === CleaningTaskStatusEnum.Complete ||
                              _.assignee?.status === CleaningTaskStatusEnum.NotApplicable,
                          ).length ?? 0;
                    const tabScheduleColor = getScheduleColor(tab.value as CleaningScheduleTypeEnum);
                    const isDone = total > 0 && completed === total;
                    const isPartial = completed > 0 && completed < total;

                    return !isEmpty(tasks) ? (
                      <ButtonGroup.Item
                        key={tab.value}
                        pr={1.5}
                        endIcon={
                          <Chip
                            value={`${completed}/${total}`}
                            _container={{
                              backgroundColor: isDone ? `${tabScheduleColor}.500` : isPartial ? `${tabScheduleColor}.50` : 'gray.100',
                              borderColor: isPartial ? `${tabScheduleColor}.200` : 'gray.200',
                              borderWidth: isDone ? 0 : 1,
                              px: 1.5,
                              py: 1,
                            }}
                            letterSpacing={1}
                            fontWeight={600}
                            textAlign="center"
                            size="sm"
                            color={isDone ? 'white' : isPartial ? `${tabScheduleColor}.700` : 'gray.500'}
                          />
                        }
                        value={tab.value}
                      >
                        {t(`cleaning_task:${tab.title}`)}
                      </ButtonGroup.Item>
                    ) : (
                      <></>
                    );
                  })}
                </ButtonGroup>
              </HStack>

              {/* Schedule + Auto-Assign + Expand on their own row (small screen) */}
              {!props.isEditable && (
                <HStack
                  bg="white"
                  px={4}
                  py={1.5}
                  borderBottomWidth={1}
                  borderColor="gray.200"
                  justifyContent="flex-end"
                  alignItems="center"
                  space={2}
                >
                  <PopoverScheduleMenu
                    compact
                    scheduleType={scheduleType}
                    isOpen={activeMenu === 'schedule'}
                    onOpen={() => setActiveMenu('schedule')}
                    onClose={() => setActiveMenu(null)}
                    copyEligibleCount={eligibleForCopyLastWeek.length}
                    bulkEligibleCount={eligibleForBulk.length}
                    onEnterCopyLastWeekMode={() => handleEnterPopupMode('copy-last-week', eligibleForCopyLastWeek)}
                    onEnterBulkMode={() => handleEnterPopupMode('bulk', eligibleForBulk)}
                  />
                  <PopoverAutoAssignMenu
                    compact
                    isOpen={activeMenu === 'auto-assign'}
                    onOpen={() => setActiveMenu('auto-assign')}
                    onClose={() => setActiveMenu(null)}
                    tasks={cleaningPlan?.tasks ?? []}
                    eligibleForAutoAssign={eligibleForAutoAssign}
                    onEnterAutoAssignMode={() => handleEnterPopupMode('auto-assign', eligibleForAutoAssign)}
                  />
                  <IconButton
                    onPress={() => onToggleIsEditable?.(scheduleType, tabFocus)}
                    accessibilityLabel={`Open ${scheduleType} edit mode`}
                    bg={`${tabColor}.100`}
                    borderRadius={6}
                    icon={<Expand size={16} color={colors?.[tabColor]?.[700] ?? colors.gray[700]} />}
                    // Temporarily allow editing even if not within the editable window
                    // as per sir tom's request to test the crossover week functionality — can re-enable the disabled state after testing
                    // isDisabled={!isPlanEditable}
                  />
                </HStack>
              )}

              {/* Month toggle pills (narrow) — only in Monthly column during crossover weeks */}
              {scheduleType === CleaningScheduleTypeEnum.Monthly && isCrossover && (
                <HStack bg="gray.50" borderBottomWidth={1} borderColor="gray.200" px={3} py={1.5} space={1}>
                  <MonthTogglePills labels={monthLabels} activeMonth={activeMonth} onPress={setActiveMonth} />
                </HStack>
              )}

              {filteredCleaningPlan && (
                <Box flex={1} overflow="hidden">
                  <CleaningTasksTable
                    key={`${scheduleType}:${filteredCleaningPlan.date.toISOString()}:${activeMonth}`}
                    isEditable={false}
                    plan={filteredCleaningPlan}
                    onOpenLinkDocument={modalCleaningTask.setOpen}
                  />
                </Box>
              )}
            </VStack>
          ) : (
            /* Large Screen Layout */
            <VStack borderColor="gray.200" overflow="hidden" h="full" borderWidth={2} rounded="xl">
              {/* Minimal header: white background, title text in tabColor.
                  Identity is signaled by the colored title text, count chip,
                  and Schedule/Auto-Assign/Expand buttons inside. */}
              <VStack bg="white" borderBottomWidth={1} borderColor="gray.200">
                <HStack py={2.5} px={4} alignItems="center" justifyContent="space-between">
                  <HStack alignItems="center" space={2}>
                    <Text size="lg" fontWeight={700} color={`${tabColor}.700`}>
                      {t(`cleaning_task:${scheduleType.toLowerCase()}_tasks`)}
                    </Text>
                    <Chip
                      value={`${completedCount}/${totalCount}`}
                      _container={{
                        backgroundColor: allDone ? `${tabColor}.500` : partial ? `${tabColor}.50` : 'gray.100',
                        borderColor: partial ? `${tabColor}.200` : 'gray.200',
                        borderWidth: allDone ? 0 : 1,
                        px: 2.5,
                        py: 1.5,
                      }}
                      letterSpacing={1}
                      fontWeight={700}
                      textAlign="center"
                      size="md"
                      color={allDone ? 'white' : partial ? `${tabColor}.700` : 'gray.500'}
                    />
                  </HStack>
                  {/* Compact Schedule + Auto-Assign + Expand button trio */}
                  <HStack space={2} alignItems="center">
                    <PopoverScheduleMenu
                      compact
                      scheduleType={scheduleType}
                      isOpen={activeMenu === 'schedule'}
                      onOpen={() => setActiveMenu('schedule')}
                      onClose={() => setActiveMenu(null)}
                      copyEligibleCount={eligibleForCopyLastWeek.length}
                      bulkEligibleCount={eligibleForBulk.length}
                      onEnterCopyLastWeekMode={() => handleEnterPopupMode('copy-last-week', eligibleForCopyLastWeek)}
                      onEnterBulkMode={() => handleEnterPopupMode('bulk', eligibleForBulk)}
                    />
                    <PopoverAutoAssignMenu
                      compact
                      isOpen={activeMenu === 'auto-assign'}
                      onOpen={() => setActiveMenu('auto-assign')}
                      onClose={() => setActiveMenu(null)}
                      tasks={cleaningPlan?.tasks ?? []}
                      eligibleForAutoAssign={eligibleForAutoAssign}
                      onEnterAutoAssignMode={() => handleEnterPopupMode('auto-assign', eligibleForAutoAssign)}
                    />

                    <IconButton
                      onPress={() => onToggleIsEditable?.(scheduleType, tabFocus)}
                      accessibilityLabel={`Open ${scheduleType} edit mode`}
                      bg={`${tabColor}.100`}
                      borderRadius={6}
                      icon={<Expand size={16} color={colors?.[tabColor]?.[700] ?? colors.gray[700]} />}
                    />
                  </HStack>
                </HStack>

                {scheduleType === CleaningScheduleTypeEnum.Daily && (
                  <Box>
                    <ButtonGroup
                      borderTopLeftRadius="none"
                      borderTopRightRadius="none"
                      borderBottomLeftRadius="none"
                      borderBottomRightRadius="none"
                      key={locale}
                      w="full"
                      value={tabFocus}
                      onChange={handleChangeDay}
                    >
                      {dayButtons.map(({ date, dateKey, completedCount: dayCompletedCount, isComplete }) => (
                        <ButtonGroup.Item
                          key={dateKey}
                          flex={1}
                          pr={1.5}
                          endIcon={
                            <CompletionIcon
                              size={4}
                              variant={isComplete ? 'complete' : dayCompletedCount > 0 ? 'partial' : 'blank'}
                              color={dayCompletedCount === 0 ? colors.gray[500] : undefined}
                            />
                          }
                          value={dateKey}
                        >
                          {formatTo(date, 'ddd')}
                        </ButtonGroup.Item>
                      ))}
                    </ButtonGroup>
                  </Box>
                )}

                {/* Month toggle pills — Monthly column only, crossover weeks only */}
                {scheduleType === CleaningScheduleTypeEnum.Monthly && isCrossover && (
                  <HStack bg="gray.50" borderTopWidth={1} borderColor="gray.200" px={3} py={1.5} space={1}>
                    <MonthTogglePills labels={monthLabels} activeMonth={activeMonth} onPress={setActiveMonth} />
                  </HStack>
                )}
              </VStack>

              {/* Task table */}
              {filteredCleaningPlan && (
                <Box flex={1} overflow="hidden">
                  <CleaningTasksTable
                    key={`${scheduleType}:${filteredCleaningPlan.date.toISOString()}:${activeMonth}`}
                    isEditable={false}
                    plan={filteredCleaningPlan}
                    onOpenLinkDocument={modalCleaningTask.setOpen}
                  />
                </Box>
              )}
            </VStack>
          )}
        </VStack>
      )}

      <Modal
        size="lg"
        _content={{ rounded: 'xl', h: '90%' }}
        hideClose
        isOpen={modalCleaningTask.isOpen}
        onClose={modalCleaningTask.setHide}
      >
        {modalCleaningTask.payload && <CleaningDocuments cleaningTask={modalCleaningTask.payload} onClose={modalCleaningTask.setHide} />}
      </Modal>

      {/* Sub-mode popup: opens the same Bulk Schedule / Copy Last Week /
          Auto-Assign panel from Edit Mode in a Modal, triggered from the
          section-header compact buttons. Reuses CleaningTasksTable with
          isEditable so the user gets the real sub-mode UI without entering
          full Edit Mode. */}
      <Modal
        size="full"
        _content={{ rounded: 'xl', maxWidth: 720, width: '85%', height: '75%', overflow: 'hidden' }}
        hideClose
        noPadding
        isOpen={!!popupMode}
        onClose={handleExitPopupMode}
      >
        {renderedPopupModeRef.current && activeCleaningPlan && (
          <CleaningTasksTable
            key={`popup:${renderedPopupModeRef.current}:${activeCleaningPlan.date.toISOString()}`}
            isEditable
            isInsideModal
            plan={filteredCleaningPlan ?? activeCleaningPlan}
            editMode={renderedPopupModeRef.current}
            selectedTaskIds={selectedTaskIds}
            onEditModeChange={setEditMode}
            onSelectionChange={setSelectedTaskIds}
            onExitEditMode={handleExitPopupMode}
            onOpenLinkDocument={modalCleaningTask.setOpen}
            onClearFilters={handleClearFilters}
          />
        )}
      </Modal>
    </VStack>
  );
};
